import { Analytics } from '@cobliteam/analytics';
import { Invites } from '@cobliteam/api-lib';
import type { Invite } from '@cobliteam/common-types';
import { Environment } from '@cobliteam/react-environments';
import {
  deleteInviteRequestFailed,
  deleteInviteRequestSucceeded,
  loadInvitesRequest,
  loadInvitesRequestFailed,
  loadInvitesRequestSucceeded,
  sendInvitesRequestFailed,
  sendInvitesRequestSucceeded,
} from '@cobliteam/store/src/Invites/actionCreators';
import type { DeleteInviteRequest, SendInvitesRequest } from '@cobliteam/store/src/Invites/actions';
import { InvitesActionTypes } from '@cobliteam/store/src/Invites/actionTypes';
import Alert from '@cobliteam/ui/components/Alert';
import { all, call, put, takeLatest } from 'redux-saga/effects';

const env = Environment.getInstance().getEnvironment();

export class InvitesSagas {
  private invitesApi: Invites;

  constructor(invitesApi: Invites) {
    this.invitesApi = invitesApi;
    this.requestLoadInvitesSaga = this.requestLoadInvitesSaga.bind(this);
    this.requestSendInvitesSaga = this.requestSendInvitesSaga.bind(this);
    this.requestDeleteInviteSaga = this.requestDeleteInviteSaga.bind(this);
  }

  public *requestLoadInvitesSaga() {
    try {
      const invites: Invite[] = yield call(this.invitesApi.getPendingInvites);
      yield put(loadInvitesRequestSucceeded(invites));
    } catch (err) {
      yield put(loadInvitesRequestFailed());
      Alert.error('Houve um erro, tente novamente mais tarde.');
    }
  }

  public *requestSendInvitesSaga(action: SendInvitesRequest) {
    try {
      const invites = action.payload;
      yield call(this.invitesApi.sendInvites, invites);
      // biome-ignore lint/complexity/noForEach: <explanation>
      invites.forEach((invite) =>
        Analytics.track('User Invite Sent', {
          email: invite.email,
          hasCustomPermission: Boolean(invite.customPermissions),
        })
      );
      yield put(sendInvitesRequestSucceeded());
      Alert.success('Convites enviados com sucesso!');
      yield put(loadInvitesRequest());
    } catch (err) {
      // @ts-ignore we need check error type to use data
      const errorMessage = JSON.parse(err.message);
      // @ts-ignore we need check error type to use data
      if (errorMessage.status === 400 && errorMessage.errors && errorMessage.errors.email_addresses) {
        // @ts-ignore we need check error type to use data
        yield put(sendInvitesRequestFailed(err));
        Alert.warning('Alguns e-mails são inválidos, verifique se estão corretos.');
      } else {
        yield put(sendInvitesRequestFailed());
        Alert.error('Houve um erro, tente novamente mais tarde.');
      }
    }
  }

  public *requestDeleteInviteSaga(action: DeleteInviteRequest) {
    try {
      const inviteToDelete = action.payload;
      yield call(this.invitesApi.deleteInvite, inviteToDelete.token);
      yield put(deleteInviteRequestSucceeded());
      Alert.success('Convite excluído com sucesso!');
      yield put(loadInvitesRequest());
    } catch (err) {
      yield put(deleteInviteRequestFailed());
      Alert.error('Houve um erro, tente novamente mais tarde.');
    }
  }
}

export function* InvitesSagasListener() {
  const invitesRequester = new Invites({ host: env.herbieApiHost });
  const invitesSagas = new InvitesSagas(invitesRequester);
  yield all([
    takeLatest(InvitesActionTypes.LOAD_INVITES_REQUEST, invitesSagas.requestLoadInvitesSaga),
    takeLatest(InvitesActionTypes.SEND_INVITES_REQUEST, invitesSagas.requestSendInvitesSaga),
    takeLatest(InvitesActionTypes.DELETE_INVITE_REQUEST, invitesSagas.requestDeleteInviteSaga),
  ]);
}
